import * as React from "react";
import {
  type DataAttributes,
  type IntercomProps,
  IntercomProvider,
  useIntercom,
} from "react-use-intercom";
import {
  type BootstrapPayload,
  getBootstrap,
} from "@/shared/bootstrap/bootContract.ts";
import { useCurrentUser } from "@/shared/queries/CurrentUser.tsx";
import type { User } from "@/shared/schema/user.ts";

// Must stay in sync with the backend's inline flag. It belongs to the page
// rather than the session, so it cannot travel in the payload.
const INLINE_PATHS = ["/edit-cv-inline", "/templafy"];

function isInlineContext(pathname: string): boolean {
  return INLINE_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

interface IntercomBootProps extends DataAttributes {
  app_id: string;
  api_base: string | undefined;
}

function intercomBootProps(
  bootstrap: BootstrapPayload,
): IntercomBootProps | undefined {
  const { intercom } = bootstrap;

  if (!intercom) {
    return undefined;
  }

  if (intercom.user_hash == null) {
    return {
      app_id: intercom.app_id,
      api_base: intercom.api_base,
    };
  }

  return {
    app_id: intercom.app_id,
    api_base: intercom.api_base,
    userId: bootstrap.current_user_id,
    userHash: intercom.user_hash,
  };
}

// Intercom wants seconds; the API serves an ISO string.
function toUnixSeconds(value: string): number | undefined {
  const milliseconds = Date.parse(value);

  return Number.isNaN(milliseconds)
    ? undefined
    : Math.floor(milliseconds / 1000);
}

interface IntercomApi {
  available: boolean;
  showNewMessage: (message: string) => void;
  update: (props: IntercomProps) => void;
}

// `useIntercom` throws outside the provider, and the provider is absent when
// Intercom is unconfigured, so callers cannot read it conditionally.
const IntercomApiContext = React.createContext<IntercomApi | null>(null);

function noop() {}

export const useIntercomAvailable = () =>
  React.use(IntercomApiContext)?.available ?? false;

export function useIntercomMessage(): (message: string) => void {
  const api = React.use(IntercomApiContext);

  return api?.available ? api.showNewMessage : noop;
}

const PublishApi: React.FC<{
  available: boolean;
  children: React.ReactNode;
}> = ({ available, children }) => {
  const { showNewMessage, update } = useIntercom();

  // Memoised for identity, not speed: consumers depend on it in effects.
  const api = React.useMemo(
    () => ({ available, showNewMessage, update }),
    [available, showNewMessage, update],
  );

  return <IntercomApiContext value={api}>{children}</IntercomApiContext>;
};

// Rails builds this with Time#to_i, so it is already in Intercom's unit, and 0
// is its failure fallback rather than a real date. It comes from the flag
// context because that is the only place the date reaches the client.
function readCompanyCreatedAt(bootstrap: BootstrapPayload): number | undefined {
  const seconds = bootstrap.unleash_context.properties.companyCreatedAt;

  return seconds > 0 ? seconds : undefined;
}

// Deps are the values it sends, so an unrelated field on the user changing
// does not re-send everything.
const UserAttributes: React.FC<{
  user: User;
  companyCreatedAt: number | undefined;
}> = ({ user, companyCreatedAt }) => {
  const api = React.use(IntercomApiContext);
  const update = api?.update;

  const { email, name, company_id, company_name, created_at, roles } = user;

  React.useEffect(() => {
    if (!update) {
      return;
    }

    const createdAt = toUnixSeconds(created_at);

    update({
      email,
      name,
      createdAt,
      // Relies on update merging rather than replacing, so the createdAt set
      // at boot survives this call.
      // The only place the company is written: Intercom documents id and name
      // as its minimum, and omitting either here would leave it unregistered.
      company: {
        companyId: company_id,
        name: company_name,
        createdAt: companyCreatedAt,
      },
      customAttributes: { User_roles: roles.join(",") },
    });
  }, [
    update,
    email,
    name,
    created_at,
    company_id,
    company_name,
    companyCreatedAt,
    roles,
  ]);

  return null;
};

// isLoggedIn is undefined until the status query answers, and false for a
// visitor Intercom still boots for - anonymously, with nothing to describe.
const CurrentUserAttributes: React.FC<{
  companyCreatedAt: number | undefined;
}> = ({ companyCreatedAt }) => {
  const { user, isLoggedIn } = useCurrentUser();

  if (!isLoggedIn || !user) {
    return null;
  }

  return <UserAttributes user={user} companyCreatedAt={companyCreatedAt} />;
};

export const IntercomHost: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const bootstrap = getBootstrap();
  const bootProps = intercomBootProps(bootstrap);

  // Starts true so a support button renders as chat rather than flashing a
  // mailto link; onLoadFailed demotes it if the widget never arrives.
  const [available, setAvailable] = React.useState(true);

  if (!bootProps || isInlineContext(window.location.pathname)) {
    return children;
  }

  const { app_id, api_base, ...autoBootProps } = bootProps;

  return (
    <IntercomProvider
      appId={app_id}
      apiBase={api_base}
      autoBoot={true}
      autoBootProps={autoBootProps}
      onLoadFailed={() => {
        setAvailable(false);
      }}
    >
      <PublishApi available={available}>
        <CurrentUserAttributes
          companyCreatedAt={readCompanyCreatedAt(bootstrap)}
        />
        {children}
      </PublishApi>
    </IntercomProvider>
  );
};
